import { useEffect, useState } from "react";
import { Navigate } from "react-router-dom";
import Layout from "../components/app/Layout";
import api, { formatError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import { Activity, AlertTriangle, CheckCircle2, Database, ShieldAlert, Zap, Play } from "lucide-react";

const SEV_COLOR = {
    low: "#71717A",
    medium: "#FBBF24",
    high: "#F97316",
    critical: "#EF4444",
};

const TABS = [
    { id: "overview", label: "Overview" },
    { id: "events", label: "Events" },
    { id: "actions", label: "Actions" },
    { id: "audit", label: "Audit Log" },
];

export default function AdminOps() {
    const { user, loading } = useAuth();
    const [tab, setTab] = useState("overview");
    const [health, setHealth] = useState(null);
    const [metrics, setMetrics] = useState(null);
    const [events, setEvents] = useState([]);
    const [audit, setAudit] = useState([]);
    const [severity, setSeverity] = useState("");
    const [proposing, setProposing] = useState(false);
    const [showPropose, setShowPropose] = useState(false);

    const reload = async () => {
        try {
            const [h, m, e, a] = await Promise.all([
                api.get("/ai/ops/health"),
                api.get("/ai/ops/metrics"),
                api.get("/ai/ops/events", { params: { limit: 100, ...(severity ? { severity } : {}) } }),
                api.get("/ai/ops/audit", { params: { limit: 200 } }),
            ]);
            setHealth(h.data); setMetrics(m.data); setEvents(e.data); setAudit(a.data);
        } catch (err) { toast.error(formatError(err)); }
    };

    useEffect(() => { if (user?.role === "admin") reload(); }, [user, severity]);
    useEffect(() => {
        if (user?.role !== "admin") return;
        const t = setInterval(reload, 15_000);
        return () => clearInterval(t);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [user, severity]);

    if (loading) return <Layout><div className="px-6 py-32 text-[#71717A]">Loading...</div></Layout>;
    if (!user) return <Navigate to="/login?redirect=/admin/ops" replace />;
    if (user.role !== "admin") return <Navigate to="/" replace />;

    return (
        <Layout>
            <div className="px-6 md:px-12 lg:px-16 pt-10 pb-32">
                <div className="flex items-center gap-3">
                    <Activity className="w-5 h-5 text-[#C9A961]" />
                    <p className="overline">Ops Supervisor</p>
                </div>
                <h1 className="font-display text-5xl md:text-6xl mt-3">CellarGlobal Ops</h1>
                <p className="text-sm text-[#A1A1AA] mt-3 max-w-2xl">
                    Monitor, detect, classify. Never modifies code without your approval. All actions audited.
                </p>

                <div className="flex gap-2 mt-12 border-b border-[#27272A]" data-testid="ops-tabs">
                    {TABS.map((t) => (
                        <button key={t.id} onClick={() => setTab(t.id)} data-testid={`ops-tab-${t.id}`}
                            className={`px-6 py-4 text-sm tracking-[0.15em] uppercase relative ${tab === t.id ? "text-[#C9A961]" : "text-[#71717A] hover:text-white"}`}>
                            {t.label}
                            {tab === t.id && <span className="absolute bottom-0 left-0 right-0 h-px bg-[#C9A961]" />}
                        </button>
                    ))}
                </div>

                <div className="mt-10">
                    {tab === "overview" && (
                        <div className="grid gap-6" data-testid="ops-overview">
                            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                                <Stat label="Mongo" value={health?.mongo?.ok ? "OK" : "DOWN"} accent={!health?.mongo?.ok} icon={Database} sub={`${health?.mongo?.latency_ms ?? "—"} ms`} />
                                <Stat label="Events (24h)" value={metrics?.total ?? 0} icon={AlertTriangle} />
                                <Stat label="Unresolved" value={health?.ops_events_unresolved ?? 0} accent={(health?.ops_events_unresolved ?? 0) > 0} />
                                <Stat label="Actions Pending" value={health?.ops_actions_pending ?? 0} accent={(health?.ops_actions_pending ?? 0) > 0} icon={Zap} />
                            </div>
                            <div className="grid md:grid-cols-2 gap-6">
                                <Card title="Severity Breakdown (24h)">
                                    <div className="space-y-3 mt-4">
                                        {["critical", "high", "medium", "low"].map((s) => (
                                            <div key={s} className="flex items-center justify-between">
                                                <div className="flex items-center gap-3">
                                                    <span className="w-2 h-2 rounded-full" style={{ background: SEV_COLOR[s] }} />
                                                    <span className="text-sm uppercase tracking-wide">{s}</span>
                                                </div>
                                                <span className="font-display text-2xl" style={{ color: SEV_COLOR[s] }}>{metrics?.by_severity?.[s] ?? 0}</span>
                                            </div>
                                        ))}
                                    </div>
                                </Card>
                                <Card title="By Event Type">
                                    {Object.keys(metrics?.by_type || {}).length === 0 ? (
                                        <div className="mt-6 flex items-center gap-2 text-[#C9A961]"><CheckCircle2 className="w-4 h-4" /> Sistema limpio, sin incidentes.</div>
                                    ) : (
                                        <div className="space-y-2 mt-4">
                                            {Object.entries(metrics?.by_type || {}).map(([k, v]) => (
                                                <div key={k} className="flex items-center justify-between text-sm">
                                                    <span className="text-[#E4E4E7]">{k}</span>
                                                    <span className="text-[#C9A961]">{v}</span>
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                </Card>
                            </div>
                            <p className="text-xs text-[#71717A] mt-2">
                                Last check: {health?.checked_at?.replace("T", " ").slice(0, 19)} UTC · Poll cada 15s
                            </p>
                        </div>
                    )}

                    {tab === "events" && (
                        <div data-testid="ops-events">
                            <div className="flex gap-3 mb-8 items-center">
                                <span className="overline">Filter:</span>
                                {["", "critical", "high", "medium", "low"].map((s) => (
                                    <button key={s || "all"} onClick={() => setSeverity(s)}
                                        className={`text-xs uppercase tracking-wide px-3 py-1.5 border ${severity === s ? "border-[#C9A961] text-[#C9A961]" : "border-[#27272A] text-[#A1A1AA] hover:text-white"}`}
                                        data-testid={`ops-filter-${s || "all"}`}>
                                        {s || "all"}
                                    </button>
                                ))}
                            </div>
                            {events.length === 0 ? (
                                <div className="border border-[#27272A] p-16 text-center">
                                    <ShieldAlert className="w-8 h-8 text-[#C9A961] mx-auto mb-4" />
                                    <p className="font-display text-2xl text-[#A1A1AA]">Sin eventos que reportar.</p>
                                </div>
                            ) : (
                                <div className="border border-[#27272A]">
                                    <table className="w-full text-sm">
                                        <thead className="bg-[#0A0A0A]">
                                            <tr className="text-left text-[#71717A] uppercase tracking-wide text-xs">
                                                <th className="py-3 px-4">Sev</th>
                                                <th className="py-3 px-4">Type</th>
                                                <th className="py-3 px-4">Message</th>
                                                <th className="py-3 px-4">Suggested Fix</th>
                                                <th className="py-3 px-4">When (UTC)</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {events.map((e) => (
                                                <tr key={e.id} className="border-t border-[#27272A]" data-testid={`ops-event-${e.id}`}>
                                                    <td className="py-3 px-4">
                                                        <span className="text-xs uppercase tracking-wide" style={{ color: SEV_COLOR[e.severity] }}>{e.severity}</span>
                                                    </td>
                                                    <td className="py-3 px-4 font-mono text-xs text-[#E4E4E7]">{e.event_type}</td>
                                                    <td className="py-3 px-4 text-[#A1A1AA] max-w-md truncate">{e.message}</td>
                                                    <td className="py-3 px-4 text-xs text-[#71717A] max-w-sm">{e.suggested_fix}</td>
                                                    <td className="py-3 px-4 text-xs text-[#71717A] whitespace-nowrap">{e.created_at?.slice(0, 19).replace("T", " ")}</td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            )}
                        </div>
                    )}

                    {tab === "actions" && (
                        <div data-testid="ops-actions">
                            <div className="flex justify-between items-center mb-8">
                                <p className="text-sm text-[#A1A1AA]">
                                    Acciones propuestas por la IA. Nada se ejecuta sin tu aprobación explícita.
                                </p>
                                <button onClick={() => setShowPropose(true)} className="btn-outline !py-2 !px-4 !text-xs" data-testid="ops-propose-open">
                                    <Play className="w-3 h-3" /> Propose Action
                                </button>
                            </div>
                            <ActionsList reload={reload} />
                            {showPropose && <ProposeModal onClose={() => setShowPropose(false)} onDone={() => { setShowPropose(false); reload(); }} proposing={proposing} setProposing={setProposing} />}
                        </div>
                    )}

                    {tab === "audit" && (
                        <div data-testid="ops-audit">
                            {audit.length === 0 ? (
                                <div className="border border-[#27272A] p-16 text-center text-[#A1A1AA]">Sin actividad registrada.</div>
                            ) : (
                                <div className="border border-[#27272A]">
                                    <table className="w-full text-sm">
                                        <thead className="bg-[#0A0A0A]">
                                            <tr className="text-left text-[#71717A] uppercase tracking-wide text-xs">
                                                <th className="py-3 px-4">When</th>
                                                <th className="py-3 px-4">Actor</th>
                                                <th className="py-3 px-4">Action</th>
                                                <th className="py-3 px-4">Detail</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {audit.map((a) => (
                                                <tr key={a.id} className="border-t border-[#27272A]" data-testid={`ops-audit-${a.id}`}>
                                                    <td className="py-3 px-4 text-xs text-[#71717A] whitespace-nowrap">{a.created_at?.slice(0, 19).replace("T", " ")}</td>
                                                    <td className="py-3 px-4 text-[#E4E4E7]">{a.actor}</td>
                                                    <td className="py-3 px-4 text-[#C9A961] uppercase tracking-wide text-xs">{a.action}</td>
                                                    <td className="py-3 px-4 text-xs text-[#A1A1AA] font-mono">{JSON.stringify(a.detail).slice(0, 140)}</td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            )}
                        </div>
                    )}
                </div>
            </div>
        </Layout>
    );
}

function Stat({ label, value, sub, accent, icon: Icon }) {
    return (
        <div className={`border p-6 ${accent ? "border-[#C9A961]" : "border-[#27272A]"}`}>
            <div className="flex items-center gap-2">
                {Icon && <Icon className={`w-4 h-4 ${accent ? "text-[#C9A961]" : "text-[#71717A]"}`} />}
                <p className="text-xs text-[#71717A] uppercase tracking-wide">{label}</p>
            </div>
            <p className={`font-display text-4xl mt-3 ${accent ? "text-[#C9A961]" : "text-white"}`}>{value}</p>
            {sub && <p className="text-xs text-[#71717A] mt-1">{sub}</p>}
        </div>
    );
}

function Card({ title, children }) {
    return (
        <div className="border border-[#27272A] p-6">
            <p className="overline">{title}</p>
            {children}
        </div>
    );
}

function ActionsList({ reload }) {
    const [items, setItems] = useState([]);
    const load = async () => {
        try {
            const { data } = await api.get("/ai/ops/audit", { params: { limit: 200 } });
            // pull the proposed actions via events collection - use a dedicated fetch instead
            const r = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/ai/ops/events`, {
                headers: { Authorization: `Bearer ${localStorage.getItem("cg_token")}` },
            });
            // ignore, actions come via approve endpoint result — for MVP list from audit
            setItems(data);
        } catch (err) { toast.error(formatError(err)); }
    };
    useEffect(() => { load(); }, []);
    const approve = async (actionId) => {
        try {
            await api.post(`/ai/ops/actions/${actionId}/approve`);
            toast.success("Action approved & executed.");
            reload(); load();
        } catch (err) { toast.error(formatError(err)); }
    };
    // Filter audit entries to show proposed actions still awaiting approval
    const proposed = items.filter((a) => a.action === "propose");
    const approvedIds = new Set(items.filter((a) => a.action === "approve").map((a) => a.detail?.action_id));
    const pending = proposed.filter((p) => !approvedIds.has(p.detail?.action_id));

    if (pending.length === 0) return (
        <div className="border border-[#27272A] p-12 text-center text-[#A1A1AA]">Sin acciones pendientes.</div>
    );
    return (
        <div className="space-y-3">
            {pending.map((p) => (
                <div key={p.id} className="border border-[#27272A] p-5 flex flex-wrap items-center justify-between gap-4" data-testid={`ops-pending-${p.detail?.action_id}`}>
                    <div>
                        <p className="text-[#C9A961] uppercase tracking-wide text-xs">{p.detail?.kind}</p>
                        <p className="text-sm text-[#E4E4E7] mt-1 font-mono">{JSON.stringify(p.detail).slice(0, 200)}</p>
                        <p className="text-xs text-[#71717A] mt-2">Propuesta: {p.created_at?.slice(0, 19).replace("T", " ")} UTC</p>
                    </div>
                    <button onClick={() => approve(p.detail?.action_id)} className="btn-gold !py-2 !px-4 !text-xs" data-testid={`ops-approve-${p.detail?.action_id}`}>
                        Approve & Execute
                    </button>
                </div>
            ))}
        </div>
    );
}

function ProposeModal({ onClose, onDone, proposing, setProposing }) {
    const [kind, setKind] = useState("create_index");
    const [collection, setCollection] = useState("wines");
    const [field, setField] = useState("region");
    const [rationale, setRationale] = useState("");

    const submit = async (e) => {
        e.preventDefault();
        setProposing(true);
        try {
            const params = kind === "create_index" ? { collection, field } : {};
            await api.post("/ai/ops/actions/propose", { kind, params, rationale });
            toast.success("Action proposed. Approve to execute.");
            onDone();
        } catch (err) { toast.error(formatError(err)); }
        finally { setProposing(false); }
    };

    return (
        <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-center justify-center p-4" data-testid="ops-propose-modal">
            <form onSubmit={submit} className="bg-[#0A0A0A] border border-[#27272A] max-w-lg w-full">
                <div className="p-8 border-b border-[#27272A]">
                    <p className="overline">Propose Ops Action</p>
                    <h3 className="font-display text-2xl mt-2">New Action Proposal</h3>
                </div>
                <div className="p-8 space-y-5">
                    <div>
                        <label className="overline block mb-2">Kind</label>
                        <select className="input-line bg-black" value={kind} onChange={(e) => setKind(e.target.value)} data-testid="ops-propose-kind">
                            <option value="create_index">create_index</option>
                            <option value="restart_backend">restart_backend</option>
                            <option value="mark_resolved">mark_resolved</option>
                        </select>
                    </div>
                    {kind === "create_index" && (
                        <>
                            <div>
                                <label className="overline block mb-2">Collection</label>
                                <input className="input-line" value={collection} onChange={(e) => setCollection(e.target.value)} data-testid="ops-propose-collection" />
                            </div>
                            <div>
                                <label className="overline block mb-2">Field</label>
                                <input className="input-line" value={field} onChange={(e) => setField(e.target.value)} data-testid="ops-propose-field" />
                            </div>
                        </>
                    )}
                    <div>
                        <label className="overline block mb-2">Rationale</label>
                        <textarea className="input-line resize-none" rows="3" value={rationale} onChange={(e) => setRationale(e.target.value)} data-testid="ops-propose-rationale" />
                    </div>
                    <div className="flex gap-3">
                        <button type="button" onClick={onClose} className="btn-ghost flex-1">Cancel</button>
                        <button type="submit" disabled={proposing} className="btn-gold flex-1" data-testid="ops-propose-submit">
                            {proposing ? "Proposing..." : "Propose"}
                        </button>
                    </div>
                </div>
            </form>
        </div>
    );
}
